import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { ConvertAvatarToBadgeIconSizePipe, ExecPipe } from "shared-pipes";
import { canOpenProfile, getAbbreviation, userIsActive } from "shared-utils";
import { FollowingStatusType, IUserViewAvatar, IUserViewAvatarProfileOpeningProps, SimpleChangesTyped } from "types";
import { userIsDeleted } from "./utils";
import { NgClass, NgStyle } from "@angular/common";
import { TooltipModule } from "primeng/tooltip";
import { AvatarSize } from "types/interfaces";
import { SvgIconComponent } from "@db-ui/svg-icon";
import { ConvertAvatarToUserIconSizePipe } from "shared/pipes/convertAvatarToUserIconSize.pipe";

@Component({
  selector: "db-user-avatar",
  templateUrl: "./user-avatar.component.html",
  styleUrls: ["./user-avatar.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    TooltipModule,
    NgStyle,
    ExecPipe,
    SvgIconComponent,
    ConvertAvatarToBadgeIconSizePipe,
    ConvertAvatarToUserIconSizePipe,
  ],
})
export class UserAvatarComponent implements OnChanges {
  readonly getAbbreviation = getAbbreviation;
  readonly FollowingStatusType = FollowingStatusType;

  readonly inactiveUserMessage = $localize`:@@common|inactive-user:Deactivated user`;
  readonly deletedUserMessage = $localize`:@@common|deleted-user:Deleted user`;
  readonly anonymousUserMessage = $localize`:@@common|anonymous-user:Anonymous user`;
  readonly unknownUserMessage = $localize`:@@common|unknown-user:Unknown user`;
  imageUrl: string = "";

  @Input() userInfo!: IUserViewAvatar;
  @Input() avatarSrc: string | null | undefined = null;
  @Input() name: string | undefined; // send if you want to display something different than first and last name provided from userInfo
  @Input() avatarSize: AvatarSize = "standard"; // NOTE: when adding new sizes, please update properly the scss (check avatar, initials and favorite-star classes)
  @Input() showFavUserIcon = false;
  @Input() isAnonymous = false;
  @Input() openProfileOnClick = false;
  @Input() authUserId: string | null = null;
  @Input() showTooltips = true;
  @Input() showUserNameTooltip = false;
  @Input() noAnonymousTooltip = false;
  @Output() profileOpening = new EventEmitter<{
    event: Event;
    userInfo: IUserViewAvatarProfileOpeningProps;
  }>();

  get canOpenProfile(): boolean {
    return this.openProfileOnClick && canOpenProfile(this.userInfo, this.authUserId);
  }

  get userIsActive(): boolean {
    return userIsActive(this.userInfo);
  }

  get userIsDeleted(): boolean {
    return userIsDeleted(this.userInfo);
  }

  get isUnknownUser(): boolean {
    return !this.userInfo?.firstName && !this.userInfo?.lastName && !this.name;
  }

  get avatarTooltip(): string {
    if (this.userIsDeleted) {
      return this.deletedUserMessage;
    }
    if (!this.userIsActive) {
      return this.inactiveUserMessage;
    }
    if ((this.userInfo?.isAnonymous || this.isAnonymous) && !this.noAnonymousTooltip) {
      return this.anonymousUserMessage;
    }
    return "";
  }

  get fullName(): string {
    if (this.name) {
      return this.name;
    }
    const parts = [this.userInfo?.firstName, this.userInfo?.lastName].filter(Boolean) as string[];
    return parts.join(" ").trim();
  }

  openProfileHandler(event: Event) {
    this.profileOpening.emit({ event, userInfo: this.userInfo });
  }

  ngOnChanges(changes: SimpleChangesTyped<UserAvatarComponent>) {
    if (changes.avatarSrc || changes.userInfo) {
      this.imageUrl =
        changes.avatarSrc?.currentValue ||
        changes.userInfo?.currentValue?.profileImage ||
        changes.userInfo?.currentValue?.avatarUrl ||
        "";
    }
  }
}
